/**
 * What gets persisted, and where.
 *
 * Moved verbatim from the vuex-persist configuration these lists used to
 * parameterise (cynqueModules/store/vuexPersistPlugins.ts, and before that the
 * Bridge plugins/vuex-persist.js). The storage keys are the ones vuex-persist
 * wrote — `vuex` is its default key — and they are kept exactly so a browser
 * that has been using the Bridge app keeps its saved page state when it lands on
 * the migrated one.
 *
 * The names are store-module names, which are also Pinia store ids, so these
 * lists do not change as modules convert.
 */

/** vuex-persist's default key. Holds the page-state modules below. */
export const LOCAL_STORAGE_KEY = 'vuex'

export const SESSION_STORAGE_KEY = 'vuex-session'

/**
 * loggedInUser holds the user's identity and is persisted to sessionStorage ON
 * PURPOSE, separately from the page-state modules below (localStorage). The auth
 * token lives in sessionStorage (services/apiClient.ts), so binding identity to
 * the same storage keeps the two in lockstep: a browser restart or a fresh tab
 * clears both together. Persisting identity to localStorage (as it used to be)
 * outlived the token, so the app restored a logged-in user with no token —
 * showing logged-in chrome and firing API calls that all 403'd before forcing a
 * logout.
 */
export const SESSION_PERSISTED_MODULES: readonly string[] = ['loggedInUser']

/**
 * Five of these names have no store module and never have had one on this branch
 * (`ordersCardTopMenuBar`, `opportunityOverviewPageActivitiesTabMSServicesPage`,
 * `opportunityMSServicesCard`,
 * `opportunitySupplierQuoteLineItemsForServiceCard`, `selectLists`). They are
 * pre-existing stale entries, harmless no-ops on save and restore, and are kept
 * here rather than tidied away so this list stays a faithful copy of the one
 * that shipped.
 *
 * ⚠ `homeOverviewPageSplitsTab` used to be listed here as a sixth such name, and
 * that was WRONG in a way worth remembering: it was a no-op for PERSISTENCE, but
 * pages/Home/Overview/Splits/[id].vue names it as `pageNameForStore`, so the page
 * itself threw on every load. "No module" and "harmless" are separate questions —
 * check whether a page RESOLVES the name before calling one of these benign. It
 * now has a real store (stores/homeOverviewPageSplitsTab.ts), renamed from the
 * orphaned `homeOverviewPagePayoutsTab`.
 */
export const LOCAL_PERSISTED_MODULES: readonly string[] = [
  'accountOverviewPage',
  // Accounts, Projects and Users were added on 2026-10-26, with the list top
  // bar's team-member filter; until then their lists forgot every choice on a
  // reload. The setup lists (Case Types, Industries, the Lead Source, Product
  // category and Contact Role lists, ...) are deliberately still absent.
  'accountsPage',
  'casesPage',
  'serviceRequestsPage',
  'caseOverviewPage',
  'serviceRequestOverviewPage',
  'contactsPage',
  'companiesPage',
  'companyLocationsCardTopMenuBar',
  'companyOverviewPageActivitiesTab',
  'companyOverviewPageOperationsTab',
  'companyOverviewPageSalesTab',
  'companyOverviewPageSalesTabContractsCard',
  'companyOverviewPageSalesTabContractMSServicesCard',
  'companyOverviewPageSalesTabMSServicesCard',
  'entityIdSerialNumber',
  'fieldSetOverviewPage',
  'groupsPage',
  'homeOverviewPageActivitiesTab',
  'homeOverviewPageOperationsTab',
  'homeOverviewPageSalesTab',
  'homeOverviewPageSplitsTab',
  'locationOverviewPage',
  'locationOverviewPageMSServicesCard',
  'tsdsPage',
  'tsdOverviewPage',
  'ordersPage',
  'ordersCardTopMenuBar',
  'orderMSServicesCard',
  'orderOverviewPage',
  'orderOverviewPageActivitiesTab',
  'orderOverviewPageOperationsTab',
  'orderOverviewPageSalesTab',
  'orderOverviewPageSalesTabContractsCard',
  'orderOverviewPageSalesTabContractMSServicesCard',
  'orderOverviewPageSalesTabMSServicesCard',
  'orderOverviewPageSalesTabOrderServiceOrderMSServicesCard',
  'opportunitiesPage',
  'opportunitiesCardTopMenuBar',
  'opportunityOverviewPageActivitiesTab',
  'opportunityOverviewPageSalesTab',
  'opportunityOverviewPageActivitiesTabMSServicesPage',
  // The OPERATIONS tab renders the ORDER Overview's card set, so it persists the
  // same FIVE entries the Order Sales tab does — one more than an opportunity
  // tab needs, because only the order silo has a ServiceOrders card.
  //
  // These replaced the four `…DealsTab…` entries on 2026-09-09, when that tab
  // stopped being the deals-only projection of the opportunity and became the
  // order. Nothing migrates the saved values: the two tabs show different
  // entities, so the old column choices and collapsed-kit ids describe rows that
  // are not here. `mergeRestoredState` deep-merges saved-over-default, so the
  // new names simply start at their `state()` defaults, and the orphaned Deals
  // keys sit inertly in the 'vuex' blob until the browser's storage is cleared.
  'opportunityOverviewPageOperationsTab',
  'opportunityOverviewPageOperationsTabMSServicesCard',
  'opportunityOverviewPageOperationsTabContractsCard',
  'opportunityOverviewPageOperationsTabContractMSServicesCard',
  'opportunityOverviewPageOperationsTabOrderServiceOrderMSServicesCard',
  'opportunityOverviewPageSalesTabContractsCard',
  'opportunityOverviewPageSalesTabMSServicesCard',
  'opportunityOverviewPageSalesTabContractMSServicesCard',
  'opportunityMSServicesCard',
  'opportunitySupplierQuoteLineItemsForServiceCard',
  'pageParams',
  'personsPage',
  'personsPageExtra',
  'casesPageExtra',
  'serviceRequestsPageExtra',
  'teamMembersPage',
  'productsPage',
  'projectsPage',
  'suppliersPage',
  'supplierOverviewPageSalesTab',
  'supplierOverviewPageActivitiesTab',
  'supplierProductsCardTopMenuBar',
  'supplierProductItemsCard',
  'supplierProductOverviewPage',
  'supplierOverviewPage',
  'selectLists',
  'settings',
  'tableFooterSettings',
  'usersPage',
]
